/**
 * Auswahl einer Auszahlungsperiode: ein Feld, das aufklappt, mit Suche.
 *
 * Tastatur: Pfeile wählen, Enter übernimmt, Escape schließt. Klick daneben
 * schließt ebenfalls.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { euro, shortDate } from "../lib/format";
import { matchesPeriodQuery } from "../lib/periodSearch";
import { StatusChip, statusLabel } from "./ui";

export interface PickerPeriod {
  id: string;
  label: string;
  dateFrom: string;
  dateTo: string;
  status: string;
  itemCount: number;
  total: string | null;
}

export function PeriodPicker({
  periods,
  selectedId,
  onSelect,
}: {
  periods: readonly PickerPeriod[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);

  const selected = periods.find((p) => p.id === selectedId) ?? null;
  const shown = useMemo(
    () => periods.filter((p) => matchesPeriodQuery(p, query, statusLabel(p.status))),
    [periods, query],
  );

  useEffect(() => {
    if (!open) return;
    setQuery("");
    const i = periods.findIndex((p) => p.id === selectedId);
    setCursor(i < 0 ? 0 : i);
    input.current?.focus();
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
    // Nur beim Aufklappen zurücksetzen, nicht bei jedem Neuladen der Liste.
  }, [open]);

  // Die markierte Zeile bleibt beim Blättern mit den Pfeilen sichtbar.
  useEffect(() => {
    list.current?.children[cursor]?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  const pick = (id: string) => {
    onSelect(id);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative max-w-2xl">
      <button
        type="button"
        className="field flex w-full items-center gap-3 text-left"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {selected ? (
          <>
            <span className="flex-1 truncate">{selected.label}</span>
            <span className="hidden text-sm text-muted sm:inline">
              {shortDate(selected.dateFrom)} bis {shortDate(selected.dateTo)}
            </span>
            <StatusChip status={selected.status} />
          </>
        ) : (
          <span className="flex-1 text-muted">Periode wählen</span>
        )}
        <span aria-hidden className="text-muted">
          ▾
        </span>
      </button>

      {open ? (
        <div className="card absolute left-0 right-0 z-20 mt-1 overflow-hidden shadow-lg">
          <div className="border-b border-line p-2">
            <input
              ref={input}
              className="field"
              placeholder="Suchen: „August“, „09.2026“, „offen“ …"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setCursor(0);
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setCursor((c) => Math.min(c + 1, shown.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setCursor((c) => Math.max(c - 1, 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  const p = shown[cursor];
                  if (p) pick(p.id);
                } else if (e.key === "Escape") {
                  setOpen(false);
                }
              }}
            />
          </div>
          {shown.length === 0 ? (
            <p className="px-4 py-3 text-sm text-muted">Keine Periode passt zur Suche.</p>
          ) : (
            <ul ref={list} role="listbox" className="max-h-80 overflow-y-auto">
              {shown.map((p, i) => (
                <li
                  key={p.id}
                  role="option"
                  aria-selected={p.id === selectedId}
                  className={`flex cursor-pointer items-center gap-3 px-4 py-2 text-sm ${
                    i === cursor ? "bg-surface" : ""
                  } ${p.id === selectedId ? "font-medium" : ""}`}
                  onMouseEnter={() => setCursor(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(p.id);
                  }}
                >
                  <span className="flex-1 truncate">{p.label}</span>
                  <span className="hidden w-32 text-muted sm:inline">
                    {shortDate(p.dateFrom)} bis {shortDate(p.dateTo)}
                  </span>
                  <span className="w-24 text-right tabular-nums">{p.total ? euro(p.total) : "–"}</span>
                  <span className="w-24 text-right">
                    <StatusChip status={p.status} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
